import { RichText } from "@graphcms/rich-text-react-renderer";
import useResourceFetcher from "./use-resource-fetcher";
import {
  Center,
  Divider,
  Spinner,
  Stack,
  UseDisclosureReturn,
  useToast,
} from "@chakra-ui/react";
import DynamicForm, {
  DynamicFormSubmitFunction,
} from "components/individuals/forms/dynamic-form";
import { richTextRenderers } from "components/individuals/mdx-components";
import { FullModal } from "layouts/full-modal";

export default function ResourceFormModal({
  slug,
  disclosure,
  newsletterText,
}: {
  slug: string;
  disclosure: UseDisclosureReturn;
  newsletterText?: string;
}) {
  const toast = useToast();
  const { resource, loading } = useResourceFetcher(slug);
  //onSubmit function to be passed into DynamicForm component
  const onSubmit: DynamicFormSubmitFunction = async (
    values,
    setSuccess,
    setError,
  ) => {
    try {
      if (values.newsletter) {
        await fetch("/api/waitlist", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: values.email ?? values["email-address"],
          }),
        })
          .then((res) => res.json())
          .then((res) => {
            toast({
              title: res.title,
              description: res.subtitle,
              status: res.success ? "success" : "warning",
              duration: 5000,
              isClosable: true,
            });
          });
      }
      const response = await fetch("/api/resource/submission", {
        method: "POST",
        body: JSON.stringify({
          formData: values,
          formSlug: resource.form.slug as string,
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message);
      }
      setSuccess(true);
    } catch (err: any) {
      setError("submit", {
        type: "manual",
        message: err.message,
      });
    }
  };

  return (
    <FullModal
      modal={{ ...disclosure }}
      contentSx={{
        maxH: "90vh",
        maxW: { base: "full", md: "80vw" },
        h: "100%",
        w: "100%",
        overflow: { base: "auto", md: "hidden" },
        m: { xs: "0", sm: "0" },
        mt: { xs: "0", sm: "0" },
        p: { xs: "0", sm: "0" },
        my: "auto",
      }}
      bodySx={{
        px: {
          xs: "0",
          sm: "0",
        },
        py: {
          xs: "0",
          sm: "0",
        },
      }}
    >
      <Stack
        flexDirection={{
          base: "column",
          md: "row",
        }}
        alignItems={{
          base: "stretch",
          md: "center",
        }}
        m="auto"
        h="full"
        maxW={{ base: "xl", md: "7xl" }}
        px={{ base: "0", md: "8" }}
        py={{ base: "8", md: "12" }}
      >
        <Stack
          spacing="6"
          flex="1"
          mr={{ base: "0", md: "4" }}
          mt={{
            base: "4",
            md: "0",
          }}
          px={{
            base: "6",
            md: "0",
          }}
        >
          {loading && (
            <Center flex="1" mx="auto" h="100%">
              <Spinner
                size="xl"
                thickness="4px"
                speed="0.65s"
                color="gray.400"
              />
            </Center>
          )}

          {resource?.content && (
            <RichText
              content={resource?.content?.raw}
              renderers={richTextRenderers}
            />
          )}
        </Stack>
        <Divider
          borderWidth={1.5}
          mt={10}
          mx="auto"
          w="80%"
          display={{
            base: "block",
            md: "none",
          }}
        />
        {!loading && (
          <DynamicForm
            sx={{
              flex: "1",
              ml: { base: "0", md: "4" },
              h: "100%",
            }}
            onClose={() => disclosure.onClose()}
            onSubmit={onSubmit}
            {...resource?.form}
            from="resource-form-modal"
            newsletterText={newsletterText}
            slug={slug}
          />
        )}
      </Stack>
    </FullModal>
  );
}
